import { TextButton } from '@creditornot/cb-button';
import { InfoCircleFilled, Trashbin } from '@creditornot/cb-icons';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { Popover } from '@creditornot/cb-popover';
import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import { DocumentDropzone } from './DocumentDropzone';

const MB_BYTES = 1024 * 1024;
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * MB_BYTES;

type DocumentUploaderProps = {
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label?: React.ReactNode;
  hideRemove?: boolean;
  onChange?: (file: File) => void;
  onRemove?: () => void;
  info?: string;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ActionBar = styled(Row)`
  justify-content: flex-start;
  gap: 8px;
`;

const LabelContainer = styled.div``;

const InfoIcon = styled(InfoCircleFilled).attrs({
  width: 12,
  height: 12,
})`
  fill: ${cbSemanticCssVars.textPrimary};
`;

const TooltipInfoIcon = styled(InfoIcon)`
  fill: ${cbSemanticCssVars.textSecondary};
`;

const PopoverContent = styled.div`
  padding: 16px;
  max-width: 400px;
  ${typographyCss.Body3}
`;

const MaxSizeHint = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DocumentUploader = ({
  inputProps,
  label,
  hideRemove = false,
  onChange,
  onRemove,
  info,
}: DocumentUploaderProps) => {
  const [documentFile, setDocumentFile] = useState<File>();
  const [error, setError] = useState<React.ReactNode>(null);

  const handleSelectFile = (file: File) => {
    if (file.size > MAX_SIZE_BYTES) {
      setError(
        <FormattedMessage
          id="compliance.kyb.file-upload.file-too-big"
          values={{
            size: Math.round((file.size / MB_BYTES) * 10) / 10,
            maxSize: MAX_SIZE_MB,
          }}
        />,
      );

      return;
    }

    setError('');
    setDocumentFile(file);

    if (onChange) {
      onChange(file);
    }
  };

  const showRemove = onRemove && !hideRemove;

  return (
    <Root>
      <ActionBar>
        <LabelContainer>{label}</LabelContainer>

        {!!info && (
          <Popover placement="top" content={<PopoverContent>{info}</PopoverContent>}>
            <TooltipInfoIcon />
          </Popover>
        )}
      </ActionBar>

      <DocumentDropzone documentFileName={documentFile?.name} onSelectFile={handleSelectFile} inputProps={inputProps} />

      <Row>
        <MaxSizeHint>
          <InfoIcon />

          <T.Body3>
            <FormattedMessage
              id="compliance.kyb.file-upload.max-file-size"
              values={{
                size: MAX_SIZE_MB,
              }}
            />
          </T.Body3>
        </MaxSizeHint>

        {showRemove && (
          <TextButton icon={<Trashbin />} onClick={onRemove}>
            <FormattedMessage id="compliance.kyb.file-upload.remove-document" />
          </TextButton>
        )}
      </Row>

      {error && <InputAlert variant="error">{error}</InputAlert>}
    </Root>
  );
};
